import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../utils/queryClient'
import { apiService } from '../utils/prefetchManager'
import { usePlatforms } from '../contexts/PlatformsContext'

// Dashboard hooks
export const useTrendingApps = () => {
  const { getActivePlatforms } = usePlatforms();
  
  return useQuery({
    queryKey: queryKeys.dashboard.trending(),
    queryFn: async () => {
      // Check session storage first
      const storedData = sessionStorage.getItem("trendingApps")
      if (storedData) {
        return JSON.parse(storedData)
      }

      // Fetch from all active platforms dynamically
      const activePlatforms = getActivePlatforms();
      const platformPromises = activePlatforms.map(platform => 
        apiService.fetchTrendingByPlatform(platform.id)
      );
      
      const platformResults = await Promise.allSettled(platformPromises);
      
      // Combine all platform data
      const combinedTrendingApps = platformResults.reduce((acc, result, index) => {
        if (result.status === "fulfilled" && result.value?.games) {
          return [...acc, ...result.value.games];
        }
        return acc;
      }, []);
      
      // Cache in session storage
      sessionStorage.setItem("trendingApps", JSON.stringify(combinedTrendingApps))
      return combinedTrendingApps
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
  })
}

export const useNewReleases = () => {
  const { getActivePlatforms } = usePlatforms();
  
  return useQuery({
    queryKey: queryKeys.dashboard.newReleases(),
    queryFn: async () => {
      // Check session storage first
      const storedData = sessionStorage.getItem("newReleases")
      if (storedData) return JSON.parse(storedData)
        
      // Fetch from all active platforms dynamically
      const activePlatforms = getActivePlatforms();
      const platformPromises = activePlatforms.map(platform => 
        apiService.fetchNewReleasesByPlatform(platform.id)
      );
      
      const platformResults = await Promise.allSettled(platformPromises);
      
      // Combine all platform data
      const combinedNewReleases = platformResults.reduce((acc, result, index) => {
        if (result.status === "fulfilled" && result.value?.games) {
          return [...acc, ...result.value.games];
        }
        return acc;
      }, []);
      
      // Cache in session storage
      sessionStorage.setItem("newReleases", JSON.stringify(combinedNewReleases))
      return combinedNewReleases
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
  })
}

export const useTrackedApps = () => {
  return useQuery({
    queryKey: queryKeys.dashboard.trackedApps(),
    queryFn: async () => {
      // Check session storage first
      const storedData = sessionStorage.getItem("trackedApps")
      if (storedData) {
        return JSON.parse(storedData).slice(0, 5)
      }

      const response = await apiService.fetchTrackedApps()
      const games = response?.games || []
      
      // Cache in session storage
      sessionStorage.setItem("trackedApps", JSON.stringify(games))
      return games.slice(0, 5)
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 15,
  })
}

// Category Analysis hooks
export const useCategoryKeywords = () => {
  return useQuery({
    queryKey: queryKeys.categories.keywords(),
    queryFn: async () => {
      const response = await apiService.fetchCategoryKeywords()
      if (response.status === 200 && response.keywords && Array.isArray(response.keywords)) {
        return response.keywords
      }
      throw new Error('Invalid data format received from API')
    },
    staleTime: 1000 * 60 * 30, // 30 minutes - keywords don't change often
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

export const useCategoryData = (category, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.categories.data(category),
    queryFn: async () => {
      if (!category) return []
      
      const response = await apiService.fetchCategoryData(category)
      if (response.status === 200 && response.games) {
        // Handle both nested and flat structures
        let gamesArray = []
        if (response.games.games && Array.isArray(response.games.games)) {
          gamesArray = response.games.games
        } else if (Array.isArray(response.games)) {
          gamesArray = response.games
        } else {
          throw new Error('Invalid games data structure received from API')
        }
        return gamesArray
      }
      throw new Error('Invalid data format received from API')
    },
    enabled: enabled && !!category,
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  })
}

// Publisher Analysis hooks
export const usePublisherData = (itemId, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.publishers.data(itemId),
    queryFn: async () => {
      if (!itemId) throw new Error("No publisher ID provided")
      
      const response = await apiService.fetchPublisherData(itemId)
      return response
    },
    enabled: enabled && !!itemId,
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 30,
  })
}

export const usePublisherAppDetails = (appId, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.publishers.appDetails(appId),
    queryFn: async () => {
      if (!appId) return null
      const response = await apiService.fetchPublisherAppDetails(appId)
      return response.details
    },
    enabled: enabled && !!appId,
    staleTime: 1000 * 60 * 20, // 20 minutes
    gcTime: 1000 * 60 * 40, // 40 minutes
  })
}

// App Tracking hooks
export const useAppTrackingData = (params = {}) => {
  return useQuery({
    queryKey: queryKeys.apps.tracking(params),
    queryFn: () => apiService.fetchAppTrackingData(params),
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 20,
  })
}

// Keyword Tracking hooks
export const usePredefinedKeywords = () => {
  return useQuery({
    queryKey: queryKeys.keywords.predefined(),
    queryFn: async () => {
      const response = await apiService.fetchPredefinedKeywords()
      return response
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

export const useKeywordSearchResults = (keyword, page = 1, platform = '', enabled = true) => {
  const { getActivePlatforms } = usePlatforms();
  
  return useQuery({
    queryKey: queryKeys.keywords.tracking(`${keyword}-${platform}-${page}`),
    queryFn: () => apiService.fetchKeywordSearchResults(keyword, page, platform),
    enabled: enabled && !!keyword,
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 20, // 20 minutes
  })
}

export const useKeywordTrackingData = (keyword, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.keywords.tracking(keyword),
    queryFn: () => apiService.fetchKeywordTrackingData(keyword),
    enabled: enabled && !!keyword,
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 30,
  })
}

// Game Dashboard hooks
export const useGameDetails = (gameId, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.games.detail(gameId),
    queryFn: () => apiService.fetchGameDetails(gameId),
    enabled: enabled && !!gameId,
    staleTime: 1000 * 60 * 20, // 20 minutes
    gcTime: 1000 * 60 * 40, // 40 minutes
  })
}

export const useGameAnalytics = (gameId, params = {}, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.games.analytics(gameId, params),
    queryFn: () => apiService.fetchGameAnalytics(gameId, params),
    enabled: enabled && !!gameId,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 20,
  })
}

// User Profile hooks
export const useUserProfile = () => {
  return useQuery({
    queryKey: queryKeys.user.profile(),
    queryFn: apiService.fetchUserProfile,
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 30,
  })
}

export const useUserPlans = () => {
  return useQuery({
    queryKey: queryKeys.user.plans(),
    queryFn: apiService.fetchUserPlans,
    staleTime: 1000 * 60 * 30, // 30 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  })
}

export const useUserPaymentMethods = () => {
  return useQuery({
    queryKey: queryKeys.user.paymentMethods(),
    queryFn: apiService.fetchUserPaymentMethods,
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 30,
  })
}

// Tab-specific hooks to prevent refetching
export const useTabData = (tabKey, queryFn, dependencies = []) => {
  const queryClient = useQueryClient()
  
  return useQuery({
    queryKey: [tabKey, ...dependencies],
    queryFn,
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    // Keep previous data when switching tabs
    placeholderData: (previousData) => previousData,
  })
}

// Mutation hooks for data updates
export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (profileData) => {
      // Implement update API call
      return apiService.updateUserProfile(profileData)
    },
    onSuccess: () => {
      // Invalidate and refetch user profile
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile() })
    },
  })
}

// Prefetch utilities
export const usePrefetchQueries = () => {
  const queryClient = useQueryClient()
  const { getActivePlatforms } = usePlatforms();
  
  const prefetchDashboard = () => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.dashboard.trending(),
      queryFn: async () => {
        const storedData = sessionStorage.getItem("trendingApps")
        if (storedData) return JSON.parse(storedData)
        
        // Fetch from all active platforms dynamically
        const activePlatforms = getActivePlatforms();
        const platformPromises = activePlatforms.map(platform => 
          apiService.fetchTrendingByPlatform(platform.id)
        );
        
        const platformResults = await Promise.allSettled(platformPromises);
        
        // Combine all platform data
        const combinedTrendingApps = platformResults.reduce((acc, result, index) => {
          if (result.status === "fulfilled" && result.value?.games) {
            return [...acc, ...result.value.games];
          }
          return acc;
        }, []);
        
        sessionStorage.setItem("trendingApps", JSON.stringify(combinedTrendingApps))
        return combinedTrendingApps
      },
      staleTime: 1000 * 60 * 5, // 5 minutes for prefetch
    })
  }
  
  const prefetchCategoryKeywords = () => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.categories.keywords(),
      queryFn: apiService.fetchCategoryKeywords,
      staleTime: 1000 * 60 * 5,
    })
  }
  
  const prefetchGameDetails = (gameId) => {
    if (!gameId) return
    queryClient.prefetchQuery({
      queryKey: queryKeys.games.detail(gameId),
      queryFn: () => apiService.fetchGameDetails(gameId),
      staleTime: 1000 * 60 * 5,
    })
  }
  
  return {
    prefetchDashboard,
    prefetchCategoryKeywords,
    prefetchGameDetails,
  }
}

// Cache management hooks
export const useCacheManagement = () => {
  const queryClient = useQueryClient()
  
  const invalidateAllDashboard = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
  }
  
  const invalidateCategory = (category) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.categories.data(category) })
  }
  
  const invalidatePublisher = (itemId) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.publishers.data(itemId) })
  }
  
  const clearAllCache = () => {
    queryClient.clear()
  }
  
  return {
    invalidateAllDashboard,
    invalidateCategory,
    invalidatePublisher,
    clearAllCache,
  }
}

export default {
  useTrendingApps,
  useNewReleases,
  useTrackedApps,
  useCategoryKeywords,
  useCategoryData,
  usePublisherData,
  usePublisherAppDetails,
  useAppTrackingData,
  usePredefinedKeywords,
  useKeywordTrackingData,
  useGameDetails,
  useGameAnalytics,
  useUserProfile,
  useUserPlans,
  useUserPaymentMethods,
  useTabData,
  useUpdateUserProfile,
  usePrefetchQueries,
  useCacheManagement,
}
